import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Brain } from 'lucide-react';
import { ANALYSIS_MESSAGES } from './mockData';

interface AIAnalysisLoaderProps {
  onComplete: () => void;
  duration?: number;
}

export function AIAnalysisLoader({ onComplete, duration = 4000 }: AIAnalysisLoaderProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % ANALYSIS_MESSAGES.length);
    }, 700);

    return () => clearInterval(messageInterval);
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (newProgress < 100) {
        requestAnimationFrame(animate);
      } else {
        setTimeout(onComplete, 300);
      }
    };
    requestAnimationFrame(animate);
  }, [duration, onComplete]);

  return (
    <div className="fixed inset-0 bg-[#063F6B] flex items-center justify-center z-50">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#124C77]/40 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[400px] h-[300px] bg-[#EAA059]/[0.04] rounded-full blur-[80px]" />
      </div>

      <div className="relative z-10 text-center max-w-xl px-6">
        {/* Icon with subtle glow */}
        <motion.div
          className="inline-block mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-[#EAA059]/20 rounded-2xl blur-xl" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-[#EAA059] to-[#d4893e] rounded-2xl flex items-center justify-center shadow-lg shadow-[#EAA059]/20">
              <Brain className="w-12 h-12 text-white" />
            </div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h2
          className="text-3xl font-semibold text-white mb-4 tracking-tight"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Analyzing Your Strategy
        </motion.h2>

        {/* Cycling messages */}
        <div className="h-8 mb-10">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessageIndex}
              className="text-lg text-slate-300/80"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {ANALYSIS_MESSAGES[currentMessageIndex].text}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Progress bar */}
        <motion.div
          className="w-full max-w-sm mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#EAA059] to-[#d4893e] rounded-full"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {Math.round(progress)}% complete
          </p>
        </motion.div>
      </div>
    </div>
  );
}
